import {Component, HostListener, OnDestroy, OnInit, ViewChild, ViewContainerRef} from "@angular/core";
import {filter, switchMap, tap} from "rxjs/operators";
import {NavigationStart, Router} from "@angular/router";
import {Constants, REDIRECT_URLS} from "./constants";
import {SessionService} from "./common/session.service";
import {Util} from "./util/util";
import {Subscription} from "rxjs";
import {CartService} from "./cart/cart.service";
import {UserService} from "./common/user.service";
import {LocaleUtil} from "./util/locale-util";
import {ShareableUtil} from "./social/shareable-util";
import {CustomerCacheService} from "../checkout/customer/customer-cache.service";
import {WelcomeModalService} from "./welcome/welcome-modal.service";
import {IShopUser} from "./session/customer-models";
import {GqlShopSessionService} from "../checkout/graphql/shop-session/gql-shop-session.service";
import {NavigationService} from "./common/navigation.service";
import * as _ from "lodash";
import {TranslateService} from "./translation/translate.service";
import {AnalyticsService} from "./analytics/analytics.service";
import {UsanaCookieService} from "./common/usana-cookie.service";
import {GqlShareListService} from "./social/share-list/gql-share-list.service";

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

    userLocale;
    @ViewChild('bottomMenu', {read: ViewContainerRef}) private bottomMenuRef: ViewContainerRef;

    private subscriptions: Subscription;

    constructor(
        private router: Router,
        private sessionService: SessionService,
        private translateService: TranslateService,
        private userService: UserService,
        private cartService: CartService,
        private container: ViewContainerRef,
        private customerCacheService: CustomerCacheService,
        private welcomeModalService: WelcomeModalService,
        private gqlShopSessionService: GqlShopSessionService,
        private analyticsService: AnalyticsService,
        private usanaCookieService: UsanaCookieService,
        private navigationService: NavigationService,
        private gqlShareListService: GqlShareListService
    ) { }

    ngOnInit(): void {
        this.sessionService.fetchShopUser()
            .pipe(
                tap(() => this.shopSessionEventBrowse()),
                switchMap(() => this.cartService.fetchCart()),
                switchMap(() => this.sessionService.retrieveCustomer(false)),
                switchMap(() => this.gqlShareListService.getShareLists())
            )
            .subscribe();

        if (Util.isMobile()) {
            this.loadBottomMenuComponent();
        }

        this.sessionService.usanaLocale$.subscribe((locale) => {
            if (locale) {
                this.userLocale = locale;
            }
        });

        this.router.events.pipe(
            filter((event) => event instanceof NavigationStart)
        ).subscribe((event: NavigationStart) => {
            this.navigationService.navigationStartSubject.update(event);
            if (!this.userLocale) {
                //This code only runs on a refresh when the NavigationStart event fires before a user with a locale is populated
                this.userService.getShopUser().subscribe((user: IShopUser) => this.modifyUrl(event.url));
            } else {
                this.modifyUrl(event.url);
            }
         });

        this.subscriptions = this.customerCacheService.customerSubject.watch().subscribe(customer => {
            const validState = !ShareableUtil.isShareableState() && !Util.isEnrollState();
            if (validState && customer?.config.freeShippingEligible && customer?.config.firstOrder) {
                this.welcomeModalService.open(customer.customerType.code);
            }
        });

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.sessionService.checkForSessionChange();
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnload() {
        if (Util.isSignupState() && this.translateService.alternateLocaleSubject.isPresent()) {
            this.translateService.resetAlternateLocale();
        }
    }

    // Main method to modify the URL
    modifyUrl(eventUrl: string) {
        //if userLocale error OR Prerender, this will stop infinite loops
        if (!this.userLocale) {
            return;
        }

        const origUrl = decodeURIComponent(eventUrl);
        // remove the hashbang from the URL (!#)
        const newUrl = origUrl.replace(/^(?:\/#)?#?!?/, '');
        if (origUrl !== newUrl) {
            this.router.navigateByUrl(newUrl);
        } else {
            this.continueUrlChecks(eventUrl);
        }
    }

    // Set locale in the URL if necessary, handles redirects and default page
    private continueUrlChecks(url: string) {
        const urlArr = url.split('/');
        let urlModified = false;
        if (urlArr[1] != this.userLocale) {
            //if url has locale prefix but is not the current locale, replace locale with current
            LocaleUtil.replaceLocale(urlArr, this.userLocale);
            urlModified = true;
        }

        if(this.shouldRedirect(urlArr)) {
            this.changeToRedirectUrl(urlArr);
            urlModified = true;
        }

        //if url consists of ONLY the locale, reroute to /locale/landing state
        if (urlArr[urlArr.length - 1] == this.userLocale) {
            urlArr.push(Constants.LANDING_STATE);
            urlModified = true;
        }

        //if route was modified to fit above requirements, reroute.  Else do nothing because route is correct
        if (urlModified) {
            this.router.navigateByUrl(urlArr.join('/'));
        }
    }

    shouldRedirect(urlArr: string[]) {
        const urlArrayNoLocale = urlArr.slice(2);
        const lastIndex = urlArrayNoLocale.length - 1;
        //remove any query params from the url for the matching
        urlArrayNoLocale[lastIndex] = urlArrayNoLocale[lastIndex]?.includes('?') ? urlArrayNoLocale[lastIndex].split('?')[0] : urlArrayNoLocale[lastIndex];
        return _.find(REDIRECT_URLS, {path: urlArrayNoLocale.join('/')});
    }

    //This function needs to be called after we have added the locale to the URL
    changeToRedirectUrl(urlArr: string[]) {
        //takes off the first part of the url ex: ["", "en-US"]
        const urlArrayLocale = urlArr.slice(0, 2);
        //take the remainder of url ex: ["signup","pc","info"]
        const urlArrayNoLocale = urlArr.slice(2);
        const lastIndex = urlArrayNoLocale.length - 1;
        //save off any query params in the url ex: ["signup","pc","info?param1=1&param2=2"]"]
        const urlParams = urlArrayNoLocale[lastIndex].split('?')[1];
        //remove any query params from the url for the matching
        urlArrayNoLocale[lastIndex] = urlArrayNoLocale[lastIndex].includes('?') ? urlArrayNoLocale[lastIndex].split('?')[0] : urlArrayNoLocale[lastIndex];
        //look up the redirect non-locale section in the list ex: "signup/pc/info"
        const redirectTo = _.find(REDIRECT_URLS, {path: urlArrayNoLocale.join('/')})?.redirectTo;
        const newUrlArr = redirectTo.split('/');
        if (urlParams) {
            //put the query params back on the end of the url ex: param1=1&param2=2
            newUrlArr[newUrlArr.length - 1] = newUrlArr[newUrlArr.length - 1] + '?' + urlParams;
        }
        //replace the urlArr passed in with the new redirect URL
        urlArr.length = 0; //clear the array
        urlArr.push(...urlArrayLocale); //add the locale section
        urlArr.push(...newUrlArr);  //add the rest of the url redirect section
    }

    async loadBottomMenuComponent() {
        this.container.clear();
        const {BottomMenuComponent} = await import('../modules/bottom-menu/bottom-menu.component');
        this.bottomMenuRef.createComponent(BottomMenuComponent);
    }

    shopSessionEventBrowse() {
        if(!Util.isPrerender(window)) {
            setTimeout(()=> {
                this.gqlShopSessionService.saveFirstShopSessionEvent().subscribe(() => {
                    this.analyticsService.loadShopEvent(this.usanaCookieService.getCookie(Constants.SHOP_SESSION_ID_COOKIE));
                });
            }, Constants.SHOP_SESSION_EVENT_BROWSE_DELAY);
        }
    }
}
